/**
 * AlertsPage Component
 *
 * Dedicated page for comprehensive alert management
 * TASK-164 Phase 2: Progressive Disclosure - Full alert system
 * TASK-256: Added RemediationWorkflowModal integration
 */

import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle, XCircle, Info, Filter, Search, Loader2 } from 'lucide-react';
import SecondaryNav from '../components/layout/SecondaryNav';
import AlertGroupCard, { DESTINATIONS } from '../components/dashboard/alerts/AlertGroupCard';
import { groupAlerts } from '../utils/groupAlerts';
import { primaryTriageCount } from '../utils/triageCounts';
import TransactionDetailModal from '../components/transaction/TransactionDetailModal';
import { RemediationWorkflowModal } from '../components/compliance-page';
// TASK-381: Live alert data from store
import { ALERT_PAGE_SIZE, useAlertStore } from '../stores/alertStore';
import { AlertLevel, AlertCategory, DashboardAlert } from '../types/dashboard';
import { TransactionAlert, WorkflowType } from '../types/compliance';
import { toast } from 'sonner';
import { useMakeTaskFromSignal } from '../hooks/useMakeTaskFromSignal';

const isTransactionAlert = (alert: DashboardAlert): alert is TransactionAlert =>
  alert.category === 'transaction_monitoring' && 'transactionId' in alert;

const AlertsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLevel, setSelectedLevel] = useState<AlertLevel | 'all'>('all');
  const [selectedCategory, setSelectedCategory] = useState<AlertCategory | 'all'>('all');

  // Modal state for transaction detail
  const [selectedAlert, setSelectedAlert] = useState<TransactionAlert | null>(null);
  const [showTransactionModal, setShowTransactionModal] = useState(false);

  // Workflow modal state (TASK-256)
  const [showWorkflowModal, setShowWorkflowModal] = useState(false);
  const [workflowAlertId, setWorkflowAlertId] = useState<string>('');
  const [workflowAlertDescription, setWorkflowAlertDescription] = useState<string>('');

  // TASK-381: Live alert data from store
  const allAlerts = useAlertStore((s) => s.alerts);
  const counts = useAlertStore((s) => s.counts);
  const storeTotal = useAlertStore((s) => s.total);
  const loading = useAlertStore((s) => s.loading);
  const error = useAlertStore((s) => s.error);
  const fetchAlerts = useAlertStore((s) => s.fetchAlerts);
  const fetchCounts = useAlertStore((s) => s.fetchCounts);
  const dismissAlert = useAlertStore((s) => s.dismiss);
  const snoozeAlert = useAlertStore((s) => s.snooze);
  const markAsRead = useAlertStore((s) => s.markAsRead);
  const makeTask = useMakeTaskFromSignal();

  useEffect(() => {
    fetchAlerts({ limit: ALERT_PAGE_SIZE });
    fetchCounts();
  }, [fetchAlerts, fetchCounts]);

  // Filter alerts based on search and filters
  const filteredAlerts = useMemo(() => {
    return allAlerts.filter((alert) => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch =
          alert.title.toLowerCase().includes(query) ||
          alert.description.toLowerCase().includes(query) ||
          alert.relatedClientName?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }

      // Level filter
      if (selectedLevel !== 'all' && alert.level !== selectedLevel) {
        return false;
      }

      // Category filter
      if (selectedCategory !== 'all' && alert.category !== selectedCategory) {
        return false;
      }

      // Don't show dismissed/resolved alerts (supports both legacy and live fields)
      if (alert.status === 'dismissed' || alert.status === 'resolved' || alert.isDismissed) {
        return false;
      }

      return true;
    });
  }, [allAlerts, searchQuery, selectedLevel, selectedCategory]);

  // TASK-603: identical causes aggregate into one actionable card.
  //
  // TASK-819: and there is now ONE list of them. The page used to split groups by SIZE —
  // multi-member groups to `AlertGroupCard`, one-member groups routed out to `AlertList`,
  // whose default heading is "Urgent Alerts". Filtering to a level shrank a group to one
  // member, so the card silently changed component and a section header materialised. The
  // advisor could not tell whether filtering had revealed something or merely moved it, and
  // on the DEFAULT view — the one they land on — no alert had a snooze, a deadline or its
  // quickActions, because those live on the per-alert card that only a filter could reach.
  const groups = useMemo(() => groupAlerts(filteredAlerts), [filteredAlerts]);

  // The undismissed rows actually loaded into the page — the denominator the "showing" line
  // uses. Deliberately NOT `storeTotal`: that is the book-wide unresolved count, and the
  // truncation notice above already reports the gap between the two (TASK-801).
  const loadedAlerts = useMemo(
    () => allAlerts.filter((a) => a.status !== 'dismissed' && a.status !== 'resolved' && !a.isDismissed),
    [allAlerts],
  );

  const dismissGroup = (memberIds: string[]) => {
    memberIds.forEach((id) => dismissAlert(id));
  };

  // Which population the tiles are actually counting — see the caption below.
  const countsAreServerWide = counts.total > 0;

  // TASK-381: Use store counts (from API) when available, fallback to client-side
  const stats = useMemo(() => {
    if (counts.total > 0) {
      return counts;
    }
    const active = allAlerts.filter((a) => a.status !== 'dismissed' && a.status !== 'resolved' && !a.isDismissed);
    return {
      total: active.length,
      critical: active.filter((a) => a.level === 'critical').length,
      high: active.filter((a) => a.level === 'high').length,
      medium: active.filter((a) => a.level === 'medium').length,
      info: active.filter((a) => a.level === 'info').length,
      unread: active.filter((a) => a.status === 'active' || (!a.isRead && !a.status)).length,
    };
  }, [allAlerts, counts]);

  // Handle alert click - mark as read + open detail modal if applicable
  const handleAlertClick = (alert: DashboardAlert) => {
    // TASK-381: Mark as read when clicked
    if (alert.status === 'active' || (!alert.isRead && !alert.status)) {
      markAsRead(alert.id);
    }

    // Check if it's a transaction monitoring alert
    if (isTransactionAlert(alert)) {
      setSelectedAlert(alert);
      setShowTransactionModal(true);
    }

    if (alert.category === 'transaction_monitoring') return;

    // Navigate to first quick action route if available
    if (alert.quickActions?.[0]?.action) {
      navigate(alert.quickActions[0].action);
      return;
    }

    // TASK-923 review: and when there is no quick action, which is the NORMAL case. The
    // 2026-09-06 walkthrough clicked a blocked-upload title and saw "no visible destination";
    // the cause is here, not in the group card. `quick_actions` is `[]` for every
    // Radar-raised row in the live database — drift, reprofile and blocked upload included —
    // so this branch never fired and the click silently did nothing at all.
    //
    // The fallback is the SAME resolver the group card uses, applied to a one-member group,
    // so a title and its group's Investigate button can never point at different surfaces for
    // one signal. A cause with no mapping still falls through to the client record rather
    // than to nowhere.
    const [asGroup] = groupAlerts([alert]);
    const destination = asGroup ? DESTINATIONS[asGroup.causeKey] : undefined;
    if (destination && asGroup) {
      navigate(destination(asGroup));
      return;
    }
    if (alert.relatedClientId) navigate(`/clients/${alert.relatedClientId}`);
  };

  // TASK-381: Handle snooze — pass through `until` from AlertCard
  const handleSnooze = (alertId: string, until: string) => {
    snoozeAlert(alertId, until);
  };

  /**
   * TASK-923: the group's disposition buttons, which did nothing at all until now.
   *
   * `AlertGroupCard` has rendered one button per server-allowed disposition since TASK-820
   * and called `onDisposition?.()` — and this page never passed a handler. The 2026-09-06
   * walkthrough recorded the consequence exactly: group Assign, group Snooze and
   * "Propose trade · suggested" each produced "no visible dialog, navigation or feedback".
   * Not a broken handler; no handler.
   *
   * What is serviced here is what this page can honestly complete. `resolve` is deliberately
   * NOT: the policy requires a rationale on every row that allows it, and a group resolve
   * with no reason field would 422 — trading a silent no-op for a silent failure. It is
   * routed to the surface that has the form.
   */
  /**
   * TASK-923 review: what a GROUP card on this page can genuinely complete.
   *
   * `resolve` is absent because the policy requires a rationale on every row that allows it,
   * and a group resolve with no reason field would 422 — a silent failure traded for a silent
   * no-op. `escalate` and `propose_trade` have no group flow at all. Naming them here renders
   * them DISABLED with a reason instead of enabled-and-toasting, which is what the live drift
   * group did: "Propose trade · suggested", clicked, nothing.
   */
  const SERVICED_GROUP_DISPOSITIONS = ['assign', 'snooze'] as const;

  const handleGroupDisposition = (
    memberIds: string[],
    disposition: string,
    arg?: string,
  ) => {
    const group = groups.find((g) => g.memberIds === memberIds || g.memberIds[0] === memberIds[0]);
    switch (disposition) {
      case 'snooze':
        if (!arg) return;   // the card opens its duration menu first
        memberIds.forEach((id) => snoozeAlert(id, arg));
        toast.success(`Snoozed until ${new Date(arg).toLocaleDateString()}`);
        return;
      case 'assign': {
        const first = group?.alerts[0];
        makeTask({
          title: group
            ? `${group.causeLabel}: ${group.clients[0]?.clientName ?? ''}`.trim()
            : 'Review signals',
          source: 'alerts',
          clientId: group?.clients[0]?.clientId,
          originGroupKey: group?.key,
          subjectSourceEvent: first?.sourceEvent ?? undefined,
          contributingAlertIds: memberIds,
          // TASK-923 review: WITHOUT one of these the task binds to nothing, and
          // `_signal_policy_context` returns None — so `task_routes.py` skips
          // `validate_disposition` altogether. The group path would have been the one place an
          // advisor COULD assign a compliance signal, silently, while the Radar and the
          // dossier correctly 403. It also leaves the task with no durable subject, which is
          // exactly what TASK-724 built `subject_key` to stop.
          //
          // The group key still travels, so a batch is still identifiable as a batch — this
          // names the signal the task is ABOUT without claiming the batch is one subject.
          primaryAlertId: memberIds[0],
          relatedEntityType: 'alert',
          relatedEntityId: memberIds[0],
        });
        return;
      }
      default:
        // Never silently. `escalate`, `resolve` and `propose_trade` are real dispositions
        // with real flows — they just do not live on a group card, and saying so is the
        // difference between a boundary and a bug.
        toast.info(
          `${disposition.replace(/_/g, ' ')} is done on the signal itself — expand the group and open a signal.`,
        );
    }
  };


  return (
    <div className="min-h-screen bg-gray-50">
      <SecondaryNav />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header — TASK-653: leads with THE primary triage number (unresolved
            alert groups), identical to the bell, radar, and Compliance. */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Alert Management</h2>
          <p className="text-gray-600">
            <span data-testid="primary-unresolved-count" className="font-semibold text-gray-900">
              {primaryTriageCount(allAlerts, counts)} unresolved alert{' '}
              {primaryTriageCount(allAlerts, counts) === 1 ? 'group' : 'groups'}
            </span>{' '}
            across your client portfolio · counts below are unresolved signals by severity
          </p>
          {/* Deep review #6: the number above is the server's, over EVERY unresolved row; the
              list below is one page of ALERT_PAGE_SIZE. Say so rather than let the two disagree
              silently — the same honesty rule MeetingsList applies at its endpoint cap.
              TASK-801: both sides are now UNRESOLVED totals. `storeTotal` used to span every
              status, so the notice that exists to warn about the gap understated it — dismissed
              rows inflated the "of N" while also consuming the page the missing rows needed. */}
          {storeTotal > allAlerts.length && (
            <p data-testid="alerts-truncation-notice" className="mt-1 text-sm text-amber-800">
              Showing the {allAlerts.length} most recent of {storeTotal} unresolved signals.
              Older ones are counted above but not listed — narrow by severity or client to
              reach them.
            </p>
          )}
        </div>

        {/*
          Stats Cards — labeled SECONDARY counts: unresolved signals by severity.

          TASK-819: the label now says which POPULATION these count, because they and the
          group badges below are two different aggregations and were read as disagreeing.
          The tiles come from the server's `counts` over EVERY unresolved row book-wide; a
          card's badge describes the members of one cause on this page. Live they read
          High 37 / Medium 10 against badges summing to High 38 / Medium 9 — both correct,
          neither labelled. Now the difference is explicable from the labels alone.
        */}
        <p className="mb-2 text-xs text-gray-500" data-testid="alerts-tiles-population">
          {/*
            Review finding 5: `stats` returns the SERVER counts only when `counts.total > 0`
            and otherwise computes over `allAlerts` — exactly what this page has loaded. A
            fixed "book-wide" caption would therefore state something false whenever the
            counts endpoint fails, which is the same class of mislabelling this change exists
            to remove. The caption follows the branch `stats` actually took.
          */}
          {countsAreServerWide
            ? 'Book-wide unresolved signals by severity — not the causes listed below, and not limited to what this page has loaded.'
            : 'Unresolved signals by severity, counted over what this page has loaded — the book-wide counts are unavailable.'}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Critical</p>
                <p className="text-3xl font-bold text-red-600">{stats.critical}</p>
              </div>
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">High</p>
                <p className="text-3xl font-bold text-orange-600">{stats.high}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-orange-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Medium</p>
                <p className="text-3xl font-bold text-yellow-600">{stats.medium}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-yellow-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Info</p>
                <p className="text-3xl font-bold text-blue-600">{stats.info}</p>
              </div>
              <Info className="w-8 h-8 text-blue-600" />
            </div>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search alerts by title, description, or client..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:border-indigo-600"
              />
            </div>

            {/* Level Filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-500" />
              <select
                value={selectedLevel}
                onChange={(e) => setSelectedLevel(e.target.value as AlertLevel | 'all')}
                aria-label="Filter by level"
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:border-indigo-600"
              >
                <option value="all">All Levels</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="info">Info</option>
              </select>
            </div>

            {/* Category Filter */}
            <div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value as AlertCategory | 'all')}
                aria-label="Filter by category"
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:border-indigo-600"
              >
                <option value="all">All Categories</option>
                <option value="compliance">Compliance</option>
                <option value="portfolio">Portfolio</option>
                <option value="document">Document</option>
                <option value="task">Task</option>
                <option value="client">Client</option>
                <option value="meeting">Meeting</option>
                <option value="transaction_monitoring">Transaction Monitoring</option>
                <option value="media_monitoring">Media Monitoring</option>
              </select>
            </div>

            {/* Clear Filters */}
            {(searchQuery || selectedLevel !== 'all' || selectedCategory !== 'all') && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedLevel('all');
                  setSelectedCategory('all');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                Clear Filters
              </button>
            )}
          </div>

          {/*
            TASK-819: ALWAYS shown, not only when a filter is set. Its absence on the default
            view is why the counter looked like it appeared out of nowhere the moment an
            advisor filtered — one more way the page seemed to change shape under a filter.
            It counts ALERTS on this page, which is deliberately a different population from
            the severity tiles below (book-wide unresolved signals) and from the group cards
            (causes). Each says which, so the three can never be read as disagreeing.
          */}
          <div className="mt-3 pt-3 border-t border-gray-200">
            <p className="text-sm text-gray-600" data-testid="alerts-showing-count">
              Showing <span className="font-semibold">{filteredAlerts.length}</span> of{' '}
              <span className="font-semibold">{loadedAlerts.length}</span> loaded alerts, in{' '}
              <span className="font-semibold">{groups.length}</span>{' '}
              {groups.length === 1 ? 'cause' : 'causes'}
            </p>
          </div>
        </div>

        {/* TASK-381: Loading state */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
            <span className="ml-3 text-gray-600">Loading alerts...</span>
          </div>
        )}

        {/* TASK-381: Error state */}
        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-red-700">Failed to load alerts: {error}</p>
            <button
              onClick={() => { fetchAlerts({ limit: ALERT_PAGE_SIZE }); fetchCounts(); }}
              className="mt-2 text-sm font-medium text-red-600 hover:text-red-800"
            >
              Retry
            </button>
          </div>
        )}

        {/* Grouped signals (TASK-603) — TASK-819: every group, one component, always. */}
        {!loading && groups.length > 0 && (
          <div className="mb-4 space-y-3">
            {groups.map((group) => (
              <AlertGroupCard
                key={group.key}
                group={group}
                onDismissGroup={dismissGroup}
                onDismissAlert={dismissAlert}
                onSnoozeAlert={handleSnooze}
                onSelectAlert={handleAlertClick}
                onDisposition={handleGroupDisposition}
                servicedDispositions={SERVICED_GROUP_DISPOSITIONS}
              />
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredAlerts.length === 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {searchQuery || selectedLevel !== 'all' || selectedCategory !== 'all'
                ? 'No alerts match your filters'
                : 'All clear!'}
            </h3>
            <p className="text-gray-600 mb-4">
              {searchQuery || selectedLevel !== 'all' || selectedCategory !== 'all'
                ? 'Try adjusting your search or filter criteria'
                : 'You have no active alerts at this time'}
            </p>
            {(searchQuery || selectedLevel !== 'all' || selectedCategory !== 'all') && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedLevel('all');
                  setSelectedCategory('all');
                }}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-md transition-colors"
              >
                Clear All Filters
              </button>
            )}
          </div>
        )}

        {/* Transaction Detail Modal */}
        {showTransactionModal && selectedAlert && (
          <TransactionDetailModal
            isOpen={showTransactionModal}
            transactionAlert={selectedAlert}
            onClose={() => {
              setShowTransactionModal(false);
              setSelectedAlert(null);
            }}
            onTriggerWorkflow={(_workflowType: WorkflowType) => {
              if (selectedAlert) {
                setWorkflowAlertId(selectedAlert.id);
                setWorkflowAlertDescription(selectedAlert.title || selectedAlert.description || 'Alert Investigation');
                setShowWorkflowModal(true);
              }
            }}
            onUploadDocument={() => {
              // TODO: Open DocumentVerificationModal (TASK-216) when complete
            }}
            onAddComment={(_comment: string) => {
              // TODO: Persist comment via API (future backend integration)
            }}
            onDismiss={() => {
              dismissAlert(selectedAlert.id);
              setShowTransactionModal(false);
              setSelectedAlert(null);
            }}
          />
        )}

        {/* Remediation Workflow Modal (TASK-256) */}
        <RemediationWorkflowModal
          isOpen={showWorkflowModal}
          alertId={workflowAlertId}
          alertDescription={workflowAlertDescription}
          onClose={() => setShowWorkflowModal(false)}
          onWorkflowComplete={() => {
            setShowWorkflowModal(false);
            // Optionally close the transaction modal and refresh
            setShowTransactionModal(false);
            setSelectedAlert(null);
          }}
        />
      </main>
    </div>
  );
};

export default AlertsPage;
